import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import Badge from './Parts/Badge';
import SideMenu from './SideMenu/SideMenu';
import { TOGGLE_MENU, OPEN_USER_INFO_DIALOG } from '../actions/actionTypes';
import {
  isRoot,
  postMessageForCansuke,
  convertCansukeFormat,
} from '../lib/tools';
import UserIcon from './Parts/UserIcon';
import logoutImage from '../images/common/logout.svg';
import { PAGE_PATH, ROLE, CANSUKE_RESULT } from '../lib/constants';
import SupportAgent from './Icons/SupportAgent';
import CallingRoomsDialog from './Remote/CallingRoomsDialog';
import closeButton from '../images/common/close.svg';
import ColorSF2 from '../styles/common/ColorSF2';
import {
  Main,
  MenuWrapper,
  TitleWrapper,
  ErrorMessage,
  ButtonsWrapper,
  ButtonWrapper,
  BadgeWrapper,
  LogoutIconImage,
  DialogBackground,
  CallingRoomsDialogWrapper,
  style,
  CloseButtonImage,
} from '../styles/common/TitleBarStyles';
import { ReactComponent as ServiceLogo } from '../images/common/logo_OPTiM-taglet.svg';

const MainTitleBar = ({
  isMobile,
  menuIsOpen,
  userInfo,
  license,
  version,
  allowRemoteSupport,
  dispatch,
  hidden,
  provider,
  innerRole,
  isCompanyAdmin,
  callingRooms,
  callingRoomsMembers,
  apiToken,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const [openCallingRoomsDialog, setOpenCallingRoomsDialog] = useState(false);
  const callingRoomsExist = callingRooms && callingRooms.length > 0;
  useEffect(() => {
    if (!callingRoomsExist && openCallingRoomsDialog) {
      setOpenCallingRoomsDialog(false);
    }
  }, [callingRoomsExist, openCallingRoomsDialog]);

  const badgeContent = callingRooms.length;
  const role = (userInfo && userInfo.role) || ROLE.INITIAL;
  const { licenseExceededItems, user, storage, remoteSupport } = license;
  const userLicenseExceeded = (user.licenseExceeded && 'userLicense') || '';
  const storageLicenseExceeded =
    (storage.licenseExceeded && 'storageLicense') || '';
  const remoteSupportLicenseExceeded =
    (remoteSupport.licenseExceeded && 'remoteSupportLicense') || '';
  const displayMessage =
    !isRoot(role) &&
    (userLicenseExceeded ||
      storageLicenseExceeded ||
      remoteSupportLicenseExceeded);
  const messageIdEnd = role === ROLE.SERVICE_VENDOR ? 'sp' : 'other';
  const licenseExceededMessageId = `mainTitleBar.message.licenseExceeded.${messageIdEnd}`;
  const excessValue = (storageLicenseExceeded && storage.excessNumber) || '';
  const storageExcessValue =
    (storageLicenseExceeded &&
      formatMessage(
        {
          id: 'mainTitleBar.message.licenseExceeded.other.storageExcessValue',
        },
        { excessValue },
      )) ||
    '';
  const excessItemArray =
    (messageIdEnd === 'other' &&
      licenseExceededItems.map(item =>
        formatMessage(
          { id: `mainTitleBar.message.licenseExceeded.other.${item}` },
          { storageExcessValue },
        ),
      )) ||
    [];
  const excessItems =
    (excessItemArray.length > 0 &&
      excessItemArray.reduce((previous, current) => `${previous}${current}`)) ||
    '';
  const isRemoteSupport =
    (remoteSupport.enabled && allowRemoteSupport) || false;
  const isCansuke = provider && provider.isCansuke;
  return (
    <Main className="mainTitleBar" hidden={hidden}>
      <MenuWrapper>
        {!isCansuke && (
          <SideMenu
            innerRole={innerRole}
            isCompanyAdmin={isCompanyAdmin}
            isMobile={isMobile}
            isRemoteSupport={isRemoteSupport}
            menuIsOpen={menuIsOpen}
            onToggleMenu={() => dispatch({ type: TOGGLE_MENU })}
            dispatch={dispatch}
            version={version}
          />
        )}
        {!isCansuke ? (
          <TitleWrapper className="button">
            <Link to="/ds/" style={style.linkTitle}>
              <ServiceLogo style={{ width: '150px', height: '40px' }} />
            </Link>
          </TitleWrapper>
        ) : (
          <TitleWrapper className="button">
            <ServiceLogo style={{ width: '150px' }} />
          </TitleWrapper>
        )}
        {displayMessage && (
          <ErrorMessage>
            <FormattedMessage
              id={licenseExceededMessageId}
              values={{ storageExcessValue, excessItems }}
            />
          </ErrorMessage>
        )}
      </MenuWrapper>
      {isCansuke ? (
        <CloseButtonImage
          id="mediaCloseButton"
          role="presentation"
          src={closeButton}
          alt="closeButton"
          className="button"
          onClick={() =>
            postMessageForCansuke(convertCansukeFormat(CANSUKE_RESULT.cancel))
          }
        />
      ) : (
        <ButtonsWrapper>
          <ButtonWrapper
            id="openCallingRoomsDialog"
            role="presentation"
            className={callingRoomsExist && 'button'}
            active={openCallingRoomsDialog}
            disabled={!callingRoomsExist}
            onClick={() => {
              if (callingRoomsExist) {
                dispatch({ type: 'RESET_CALLING_ROOMS_INFO' });
                setOpenCallingRoomsDialog(!openCallingRoomsDialog);
              }
            }}
          >
            <SupportAgent
              color={
                (callingRoomsExist && ColorSF2.navyBlue) || ColorSF2.lightBlue
              }
            />
            <FormattedMessage id="mainTitleBar.button.callingRooms" />
          </ButtonWrapper>
          <BadgeWrapper>
            <Badge badgeContent={badgeContent} max={9} />
          </BadgeWrapper>
          <ButtonWrapper
            id="logoutButton"
            role="presentation"
            className="button"
            // onClick={() => dispatch({ type: LOGOUT })}
          >
            <Link to={PAGE_PATH.DS_LOGOUT} style={style.linkLogout}>
              <LogoutIconImage src={logoutImage} alt="logout" />
              <FormattedMessage id="logout.button.name" />
            </Link>
          </ButtonWrapper>
          <ButtonWrapper
            id="openUserInfoDialog"
            role="presentation"
            className="button"
            onClick={() => dispatch({ type: OPEN_USER_INFO_DIALOG })}
          >
            <UserIcon url={userInfo.iconUrl} />
          </ButtonWrapper>
        </ButtonsWrapper>
      )}
      {openCallingRoomsDialog && (
        <Fragment key="openCallingRoomsDialog">
          <DialogBackground onClick={() => setOpenCallingRoomsDialog(false)} />
          <CallingRoomsDialogWrapper>
            <CallingRoomsDialog
              dispatch={dispatch}
              apiToken={apiToken}
              userInfo={userInfo}
              callingRoomsMembers={callingRoomsMembers}
              callingRooms={callingRooms}
              onClose={() => setOpenCallingRoomsDialog(false)}
            />
          </CallingRoomsDialogWrapper>
        </Fragment>
      )}
    </Main>
  );
};

MainTitleBar.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  menuIsOpen: PropTypes.bool.isRequired,
  userInfo: PropTypes.object.isRequired,
  license: PropTypes.object.isRequired,
  version: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  allowRemoteSupport: PropTypes.bool.isRequired,
  hidden: PropTypes.bool,
  provider: PropTypes.object.isRequired,
  innerRole: PropTypes.number.isRequired,
  isCompanyAdmin: PropTypes.bool.isRequired,
  callingRooms: PropTypes.array.isRequired,
  callingRoomsMembers: PropTypes.array.isRequired,
  apiToken: PropTypes.string.isRequired,
};
MainTitleBar.defaultProps = {
  hidden: false,
};
export default MainTitleBar;
